/**
 * Utilitários de Mermaid compartilhados entre o componente de tela (Mermaid.tsx)
 * e as exportações (HTML/PDF).
 *
 * Para exportar, um diagrama Mermaid precisa virar **imagem raster (PNG)** — SVG
 * inline não sobrevive bem ao html2canvas (usado no PDF) e pode ficar ilegível.
 * Aqui renderizamos o diagrama, transformamos o SVG em PNG e trocamos por `<img>`.
 *
 * `htmlLabels: false` é essencial: com labels em HTML o mermaid usa `foreignObject`,
 * que não rasteriza (some o texto). Forçando labels em SVG `<text>`, a imagem sai
 * completa.
 */

type MermaidApi = {
  initialize: (config: Record<string, unknown>) => void;
  parse: (text: string, opts?: { suppressErrors?: boolean }) => Promise<boolean | undefined>;
  render: (id: string, text: string) => Promise<{ svg: string }>;
};

let mermaidPromise: Promise<MermaidApi> | null = null;

export function loadMermaid(): Promise<MermaidApi> {
  if (!mermaidPromise) {
    mermaidPromise = import("mermaid").then((mod) => {
      const mm = (mod.default ?? mod) as unknown as MermaidApi;
      mm.initialize({
        startOnLoad: false,
        securityLevel: "strict",
        theme: "neutral",
        fontFamily: "inherit",
        // labels em SVG (não HTML) — necessário pra rasterizar em imagem
        htmlLabels: false,
        flowchart: { htmlLabels: false },
      });
      return mm;
    });
  }
  return mermaidPromise;
}

let idSeq = 0;

/** Renderiza o código Mermaid para uma string SVG. Devolve null se inválido. */
export async function renderMermaidSvg(code: string): Promise<string | null> {
  const trimmed = code.trim();
  if (!trimmed) return null;
  try {
    const mm = await loadMermaid();
    let valid = true;
    try {
      const ok = await mm.parse(trimmed, { suppressErrors: true });
      valid = ok !== false;
    } catch {
      valid = false;
    }
    if (!valid) return null;
    const { svg } = await mm.render(`mmd-export-${++idSeq}`, trimmed);
    return svg;
  } catch {
    return null;
  }
}

/** Extrai largura/altura de um SVG (preferindo o viewBox). */
function svgSize(svg: SVGElement): { width: number; height: number } {
  const vb = svg.getAttribute("viewBox");
  if (vb) {
    const p = vb.split(/[\s,]+/).map(Number);
    const w = p[2];
    const h = p[3];
    if (w !== undefined && h !== undefined && w > 0 && h > 0) {
      return { width: w, height: h };
    }
  }
  const w = parseFloat(svg.getAttribute("width") ?? "");
  const h = parseFloat(svg.getAttribute("height") ?? "");
  const wOk = !Number.isNaN(w) && !(svg.getAttribute("width") ?? "").includes("%");
  const hOk = !Number.isNaN(h) && !(svg.getAttribute("height") ?? "").includes("%");
  return { width: wOk ? w : 800, height: hOk ? h : 600 };
}

export interface RasterImage {
  dataUrl: string;
  width: number;
  height: number;
}

/** Converte um elemento `<svg>` já no DOM em PNG (fundo branco, 2x). */
export async function svgElementToPng(svgEl: SVGElement, scale = 2): Promise<RasterImage | null> {
  try {
    const { width, height } = svgSize(svgEl);
    // clona e fixa dimensões explícitas pra imagem ter tamanho natural
    const clone = svgEl.cloneNode(true) as SVGElement;
    clone.setAttribute("width", String(width));
    clone.setAttribute("height", String(height));
    clone.removeAttribute("style");
    const svgString = new XMLSerializer().serializeToString(clone);

    const img = new Image();
    img.decoding = "async";
    const src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgString)}`;
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error("svg load"));
      img.src = src;
    });

    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const ctx = canvas.getContext("2d");
    if (!ctx) return null;
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.drawImage(img, 0, 0, width, height);

    return { dataUrl: canvas.toDataURL("image/png"), width, height };
  } catch {
    return null;
  }
}

/** Renderiza código Mermaid e devolve um PNG (ou null se falhar). */
export async function mermaidCodeToPng(code: string, scale = 2): Promise<RasterImage | null> {
  const svg = await renderMermaidSvg(code);
  if (!svg) return null;
  const doc = new DOMParser().parseFromString(svg, "image/svg+xml");
  const el = doc.documentElement as unknown as SVGElement;
  if (!el || el.nodeName.toLowerCase() !== "svg") return null;
  return svgElementToPng(el, scale);
}

export type MermaidExportFormat = "png" | "svg";

function buildImg(img: RasterImage): HTMLImageElement {
  const el = document.createElement("img");
  el.className = "mermaid-img";
  el.src = img.dataUrl;
  el.style.maxWidth = "100%";
  el.style.height = "auto";
  el.style.display = "block";
  el.style.margin = "12px auto";
  // dimensões CSS pra manter proporção sem estourar
  el.width = img.width;
  el.height = img.height;
  return el;
}

/** Embrulha uma string SVG num nó inline, escalável e centralizado. */
function buildSvgNode(svgString: string): HTMLElement | null {
  const doc = new DOMParser().parseFromString(svgString, "image/svg+xml");
  const el = doc.documentElement;
  if (!el || el.nodeName.toLowerCase() !== "svg") return null;
  const svg = document.importNode(el, true) as unknown as SVGElement;
  // remove largura fixa pra deixar o max-width escalar; mantém viewBox p/ proporção
  svg.removeAttribute("width");
  svg.removeAttribute("height");
  svg.setAttribute("style", "max-width:100%;height:auto;");
  const wrap = document.createElement("div");
  wrap.className = "mermaid-img";
  // fundo branco fixo: o diagrama neutral tem traços escuros e ficaria ilegível
  // sobre o dark-mode do HTML exportado.
  wrap.setAttribute(
    "style",
    "margin:12px auto;padding:10px;text-align:center;background:#ffffff;border-radius:6px;",
  );
  wrap.appendChild(svg);
  return wrap;
}

/** Renderiza um código Mermaid no formato pedido e devolve o nó a inserir. */
async function nodeFromCode(text: string, format: MermaidExportFormat): Promise<Node | null> {
  if (format === "svg") {
    const svg = await renderMermaidSvg(text);
    return svg ? buildSvgNode(svg) : null;
  }
  const img = await mermaidCodeToPng(text);
  return img ? buildImg(img) : null;
}

/**
 * Percorre `root` e troca todo diagrama Mermaid por uma imagem legível:
 *  - `format: "svg"` → SVG inline (vetorial; ideal pra HTML exportado);
 *  - `format: "png"` → PNG raster (ideal pra PDF via html2canvas).
 *
 * Cobre blocos de código ` ```mermaid `, diagramas já renderizados na tela
 * (`.mermaid-diagram > svg`) e fallbacks (`.mermaid-fallback`).
 * Silencioso em caso de falha: o diagrama que não converter fica como estava.
 */
export async function inlineMermaidAsImages(
  root: HTMLElement,
  format: MermaidExportFormat = "png",
): Promise<void> {
  const jobs: Promise<void>[] = [];
  const replace = (target: Element, node: Node | null) => {
    if (node && target.parentNode) target.parentNode.replaceChild(node, target);
  };

  // 1) blocos de código mermaid (markdown re-renderizado nas exportações)
  for (const code of Array.from(root.querySelectorAll<HTMLElement>("code.language-mermaid"))) {
    const pre = code.closest("pre") ?? code;
    const text = code.textContent ?? "";
    jobs.push(nodeFromCode(text, format).then((node) => replace(pre, node)));
  }

  // 2) diagramas já renderizados na tela (SVG inline no balão)
  for (const container of Array.from(root.querySelectorAll<HTMLElement>(".mermaid-diagram"))) {
    const svg = container.querySelector("svg");
    if (!svg) continue;
    if (format === "svg") {
      const node = buildSvgNode(new XMLSerializer().serializeToString(svg));
      replace(container, node);
    } else {
      jobs.push(svgElementToPng(svg).then((img) => replace(container, img ? buildImg(img) : null)));
    }
  }

  // 3) fallback de código (diagrama que ainda não tinha renderizado na tela)
  for (const fallback of Array.from(root.querySelectorAll<HTMLElement>(".mermaid-fallback"))) {
    const text = fallback.textContent ?? "";
    jobs.push(nodeFromCode(text, format).then((node) => replace(fallback, node)));
  }

  await Promise.all(jobs);
}
